import type { ComputedRef } from "vue";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  Well_Test_Liquid_Input_Options_Enum,
  Well_Gas_Type_Options_Enum
} from "gql/api";
import type { DeviationSurveyEntryRows } from "../calculationTypes";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import type { FlowRates } from "@/libs/calculations/productionRates";
import {
  getGasMolecularWeight,
  getGasSpecificGravity
} from "@/libs/calculations/productionRates";
import { computed } from "vue";
import { gas_phase_gas_rate_from_glr } from "@/libs/calculations/gasPhasePVT";
import { getOilFlowRateSTDB } from "@/libs/calculations/oilPhasePVT";
import { getCasingInteriorDiameter } from "@/libs/calculations/pressureDropCorrelations";
import { getWaterFlowRateSTDB } from "@/libs/calculations/waterPhasePVT";
import { DensityUnits } from "@/libs/units/density";
import {
  ThousandCubicFeetPerDay,
  CubicFeetPerDay
} from "@/libs/units/flowRate";
import { inclinationBetweenPointsDeg } from "@/libs/calculations/deviationSurvey";
import { Ratio } from "@/libs/units/percentage";
import { HB_ADJUSTMENT, ORK_ADJUSTMENT } from "@/libs/calculations/constants";
import { clamp } from "@/utils/utils";
import { useWellTestInput } from "@/composables/forms/useWellTestInput";
import { buildLagrangeDataStructure } from "../lagrangeInterpolation";
import type { WellInputsCtx } from "./context";

// Well test inputs, deviation survey rows, temperatures, basic data and flow rates.
export const defineWellInputs = (ctx: WellInputsCtx) => {
  const {
    _wellProperties,
    _parameters,
    _deviationSurvey,
    isScenarioEspShore,
    tubingLengthFt,
    bottomOfPumpsDepthFt,
    bottomOfIntakesDepthFt,
    bottomOfSealsDepthFt,
    bottomOfMotorDepthFt,
    bottomOfEquipmentDepthFt,
    casingInteriorDiameterAtIntakeIn
  } = ctx;

  const {
    productivityIndexStbdPsia,
    totalLiquidFlowRateStbd,
    gasLiquidRatioScfStb,
    gasOilRatioScfStb,
    waterCutPercentage,
    datumPointFt,
    staticFluidLevelFeet,
    producingFluidLevelFeet,
    staticBottomholePressurePsia,
    producingBottomholePressurePsia,
    liquidDensityLbmFt3
  } = useWellTestInput(_wellProperties, _parameters);

  const tempGrad = computed(() => {
    return basicData.value.datumPointFt <= 0
      ? 0
      : (_wellProperties.value?.bottomhole_temperature_fahrenheit -
          surfaceTemperatureF.value) /
          basicData.value.datumPointFt;
  });

  const customFluidProperties = computed(() => {
    if (_wellProperties.value?.custom_fluid_properties) {
      return buildLagrangeDataStructure(
        _wellProperties.value?.custom_fluid_properties
      );
    }
    return null;
  });

  const customViscosityProperties = computed(() => {
    if (_wellProperties.value?.custom_viscosities_properties) {
      return buildLagrangeDataStructure(
        _wellProperties.value?.custom_viscosities_properties
      );
    }
    return null;
  });

  const liquidDensityAtDatumLbmft3 = computed(() => liquidDensityLbmFt3.value);

  const hasDeviationSurvey = computed(() => {
    return !!(
      !_deviationSurvey.value?.deleted_at && _deviationSurvey.value?.entry_rows
    );
  });

  const defaultEntryRows = computed(() => {
    const topOfPerfsMDFt =
      _wellProperties.value?.top_of_perforations_measured_depth_feet || 0;
    const topOfPerfsTVDFt =
      _wellProperties.value?.top_of_perforations_true_vertical_depth_feet || 0;

    // Calc angle between first row at surface (0s) and our perfs
    const fallbackInclinationDeg =
      topOfPerfsMDFt > 0
        ? inclinationBetweenPointsDeg(0, topOfPerfsMDFt, 0, topOfPerfsTVDFt)
        : 0;

    return [
      [0, 0, 0, 0, 0],
      [topOfPerfsMDFt, fallbackInclinationDeg, 0, topOfPerfsTVDFt, 0]
    ];
  });

  const entryRows = computed((): DeviationSurveyEntryRows => {
    if (
      !_deviationSurvey.value?.deleted_at &&
      _deviationSurvey.value?.entry_rows
    ) {
      // Define a mapping from directional_data_set to surveyType
      const surveyTypeMapping = {
        [Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth]:
          Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth,
        [Deviation_Survey_Directional_Dataset_Enum.TwoDMdInclination]:
          Deviation_Survey_Directional_Dataset_Enum.TwoDMdInclination,
        [Deviation_Survey_Directional_Dataset_Enum.TwoDMdTvd]:
          Deviation_Survey_Directional_Dataset_Enum.TwoDMdTvd
      };

      // Use the mapping to get the surveyType, defaulting to null if not found
      const surveyType =
        surveyTypeMapping[_deviationSurvey.value.directional_data_set] || null;

      // Set the entry_rows with the determined surveyType
      return {
        data: _deviationSurvey.value.entry_rows,
        surveyType
      };
    }

    // If we can't determine deviationSurvey type or no rows/deleted, then give default value
    return {
      data: defaultEntryRows.value,
      surveyType: null
    };
  });

  // Deviation survey rows from surface to bottom of equipment
  const entryRowsSurfaceToBoe = computed(() =>
    entryRows.value.data.filter(([measuredDepth]: number[]) => {
      return (
        measuredDepth >= 0 && measuredDepth <= bottomOfEquipmentDepthFt.value
      );
    })
  );

  // Deviation survey rows from bottom of tubing to bottom of equipment
  const entryRowsBotToBoe = computed(() =>
    entryRows.value.data.filter(([measuredDepth]: number[]) => {
      return (
        measuredDepth >= tubingLengthFt.value &&
        measuredDepth <= bottomOfEquipmentDepthFt.value
      );
    })
  );

  const entryRowsAtSeals = computed(() =>
    entryRows.value.data.filter(([measuredDepth]: number[]) => {
      return (
        measuredDepth >= bottomOfIntakesDepthFt.value &&
        measuredDepth <= bottomOfSealsDepthFt.value
      );
    })
  );

  const bottomholeTemperatureF = computed(() => {
    const temp = isScenarioEspShore.value
      ? _wellProperties.value?.bottomhole_temperature_fahrenheit
      : _wellProperties.value?.hps_pump_inlet_temperature_fahrenheit;
    return temp;
  });

  const surfaceTemperatureF = computed(() => {
    const temp = isScenarioEspShore.value
      ? _wellProperties.value?.surface_temperature_fahrenheit
      : _wellProperties.value?.hps_pump_inlet_temperature_fahrenheit;
    return temp;
  });

  // q_l/Q_l Desired liquid flow rate
  // Also known as producedFlowRate and targetFlowRate
  // These are different rates from the well tests
  // since it looks at the Total Liquid Rate from parameters first
  const desiredLiquidRateSTDB = computed(() =>
    isNaN(_parameters.value?.target_liquid_rate_std_barrel_day)
      ? NaN
      : _parameters.value?.target_liquid_rate_std_barrel_day ||
        totalLiquidFlowRateStbd.value
  );

  // WC %
  // WC should never be 100, but users should be able
  // to input 100. Therefore, use the computed ref to clamp the value
  // Never use wellProperties.value?.well_test_water_cut_percentage directly
  const waterCutRatio = computed(() => {
    const waterCutPct = clamp(waterCutPercentage.value, 0, 100);
    return Ratio.toThisUnit(waterCutPct);
  });

  // S_water
  const waterSpecificGravity: ComputedRef<number> = computed(
    () => _wellProperties.value?.water_specific_gravity || 1
  );

  // S_oil
  const oilAPI: ComputedRef<number> = computed(
    () => _wellProperties.value?.oil_gravity_api || 1
  );

  const oilSpecificGravity = computed(() =>
    DensityUnits.SpecificGravity.toThisUnit(
      DensityUnits.API.toStandardUnit(oilAPI.value)
    )
  );

  const adjustedStaticBottomholePressurePSIA = computed(() => {
    return isScenarioEspShore.value
      ? staticBottomholePressurePsia.value
      : 10000;
  });

  // Data from basic user input and is used in most calculations
  const basicData = computed<BasicData>(() => ({
    oilAPI: oilAPI.value,
    oilSpecificGravity: oilSpecificGravity.value,
    waterSpecificGravity: waterSpecificGravity.value,
    topCasingInteriorDiameterIn:
      _wellProperties.value?.r_top_casing?.interior_diameter_inches || 0,
    bottomCasingInteriorDiameterIn:
      _wellProperties.value?.r_bottom_casing?.interior_diameter_inches || 0,
    topCasingLengthFt: _wellProperties.value?.top_casing_length_feet || 0,
    bottomCasingLengthFt: _wellProperties.value?.bottom_casing_length_feet || 0,
    topTubingInteriorDiameterIn:
      _wellProperties.value?.r_top_tubing?.interior_diameter_inches || 0,
    bottomTubingInteriorDiameterIn:
      _wellProperties.value?.r_bottom_tubing?.interior_diameter_inches || 0,
    casingInteriorDiameterAtIntakeIn: casingInteriorDiameterAtIntakeIn.value,
    topTubingLengthFt: _wellProperties.value?.top_tubing_length_feet || 0,
    bottomTubingLengthFt: _wellProperties.value?.bottom_tubing_length_feet || 0,
    totalTubingLengthFt: tubingLengthFt.value,
    bottomholeTemperatureF: bottomholeTemperatureF.value,
    surfaceTemperatureF: surfaceTemperatureF.value,
    bottomTubingPressurePSIA: isScenarioEspShore.value
      ? _parameters.value?.bottom_tubing_pressure_psia || 0
      : _wellProperties.value?.hps_pump_inlet_pressure_psia,
    bottomCasingPressurePSIA:
      _parameters.value?.bottom_casing_pressure_psia || 0,
    staticBottomholePressurePSIA: adjustedStaticBottomholePressurePSIA.value,
    producingBottomholePressurePSIA: producingBottomholePressurePsia.value,
    staticFluidLevelFeet: staticFluidLevelFeet.value,
    producingFluidLevelFeet: producingFluidLevelFeet.value,
    datumPointFt:
      datumPointFt.value ||
      _wellProperties.value?.top_of_perforations_measured_depth_feet ||
      tubingLengthFt.value,
    topOfPerforationsMDFt:
      _wellProperties.value?.top_of_perforations_measured_depth_feet,
    topOfPerforationsTVDFt:
      _wellProperties.value?.top_of_perforations_true_vertical_depth_feet,
    bottomOfPumpsDepthFt: bottomOfPumpsDepthFt.value,
    hbAdjustment: HB_ADJUSTMENT,
    orkAdjustment: ORK_ADJUSTMENT,
    gasSpecificGravityOverride: _wellProperties.value?.gas_specific_gravity,
    gasSpecificGravity: gasSpecificGravity.value,
    gasMolecularWeight: gasMolecularWeight.value,
    gasPercentages: gasPercentages.value,
    averageDls: _wellProperties.value?.average_dls,
    gasType:
      (_wellProperties.value?.gas_type as Well_Gas_Type_Options_Enum) ??
      Well_Gas_Type_Options_Enum.Dry,
    hasGasCompositeValues: hasGasCompositeValues.value
  }));

  const casingInteriorDiameterAtSealsIn = computed<number>(() =>
    getCasingInteriorDiameter(_wellProperties.value, bottomOfSealsDepthFt.value)
  );

  const casingInteriorDiameterAtMotorIn = computed<number>(() =>
    getCasingInteriorDiameter(_wellProperties.value, bottomOfMotorDepthFt.value)
  );

  // --------------------------------------------------------------------------------------------------
  // these equations come from the FLUID_SCHTUFF_GAS_EDITION__1 spreadsheet:
  // static/Fluid_Schutff_Gas_Edition__1_.xlsx
  const gasPercentages = computed(() => {
    return {
      h2SHydrogenSulfidePercent: _wellProperties.value?.h2s_percent || 0,
      cO2CarbonDioxidePercent: _wellProperties.value?.co2_percent || 0,
      n2NitrogenPercent: _wellProperties.value?.n2_percent || 0,
      c1MethanePercent: _wellProperties.value?.c1_methane_percent || 0,
      c2EthanePercent: _wellProperties.value?.c2_ethane_percent || 0,
      c3PropanePercent: _wellProperties.value?.c3_propane_percent || 0,
      ic4IsobutenePercent: _wellProperties.value?.ic4_isobutene_percent || 0,
      nc4ButanePercent: _wellProperties.value?.nc4_butane_percent || 0,
      ic5IsopentanePercent: _wellProperties.value?.ic5_isopentane_percent || 0,
      nc5BentanePercent: _wellProperties.value?.nc5_bentane_percent || 0,
      c6HexanePercent: _wellProperties.value?.c6_hexane_percent || 0,
      c7HeptanePercent: _wellProperties.value?.c7_heptane_percent || 0,
      o2OxygenPercent: _wellProperties.value?.o2_percent || 0,
      arArgonPercent: _wellProperties.value?.ar_percent || 0
    };
  });

  const hasGasCompositeValues = computed(() =>
    Object.entries(gasPercentages.value).some(
      ([key, value]) =>
        ![
          "h2SHydrogenSulfidePercent",
          "cO2CarbonDioxidePercent",
          "n2NitrogenPercent"
        ].includes(key) && (value ?? 0) > 0
    )
  );

  // D33: =IF(D8>0,D8*D31,SUM(J3:J14))
  const gasMolecularWeight = computed<number>(() =>
    getGasMolecularWeight(
      gasPercentages.value,
      _wellProperties.value?.gas_specific_gravity
    )
  );

  // D32: =D33/D31
  const gasSpecificGravity = computed<number>(() =>
    getGasSpecificGravity(gasMolecularWeight.value)
  );

  // --------------------------------------------------------------------------------------------------

  // User given surface flow rates from well test
  const wellTestFlowRates = computed<FlowRates>(() => {
    if (
      _wellProperties.value?.well_test_liquid_input_type !==
      Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
    ) {
      return {
        gasFlowRateSCFD: CubicFeetPerDay.toThisUnit(
          ThousandCubicFeetPerDay.toStandardUnit(
            _wellProperties.value?.well_test_gas_rate_mscf_day
          )
        ),
        oilFlowRateSTBD: _wellProperties.value?.well_test_oil_rate_stb_day,
        waterFlowRateSTBD: _wellProperties.value?.well_test_water_rate_stb_day,
        liquidFlowRateSTBD:
          _wellProperties.value?.well_test_oil_rate_stb_day +
          _wellProperties.value?.well_test_water_rate_stb_day,
        gasOilRatioSCFSTB: gasOilRatioScfStb.value,
        gasLiquidRatioSCFSTB: gasLiquidRatioScfStb.value,
        // waterCutRatio is adjusted behind the scenes, waterCutPercentage
        // is straight from the well test input
        waterCutRatio: waterCutRatio.value,
        waterCutPercentage: waterCutPercentage.value,
        gasInOil: 0,
        gasInWater: 0,
        separatedGas: 0,
        freeGas: 0
      };
    } else {
      // We're in PI mode so use thetarget rate
      const rate = _parameters.value?.target_liquid_rate_std_barrel_day || 0;
      const waterRate = getWaterFlowRateSTDB(rate, waterCutRatio.value);
      const oilRate = getOilFlowRateSTDB(rate, waterCutRatio.value);
      const gasFlowRateSCFD = gas_phase_gas_rate_from_glr(
        rate,
        gasLiquidRatioScfStb.value
      );
      const liquidRate = oilRate + waterRate;
      const gasOilRatioSCFSTB = oilRate === 0 ? 0 : gasFlowRateSCFD / oilRate;
      const gasLiquidRatioSCFSTB = gasFlowRateSCFD / liquidRate;

      return {
        gasFlowRateSCFD,
        oilFlowRateSTBD: oilRate,
        waterFlowRateSTBD: waterRate,
        liquidFlowRateSTBD: liquidRate,
        gasOilRatioSCFSTB,
        gasLiquidRatioSCFSTB,
        waterCutRatio: waterCutRatio.value,
        waterCutPercentage: waterCutPercentage.value,
        gasInOil: 0,
        gasInWater: 0,
        separatedGas: 0,
        freeGas: 0
      };
    }
  });

  // Target surface rates derived from well test data and desired (target)
  // liquid rate
  const desiredFlowRates = computed<FlowRates>(() => {
    if (!isScenarioEspShore.value) {
      return wellTestFlowRates.value;
    }
    // Q_w
    const waterFlowRateSTBD = getWaterFlowRateSTDB(
      desiredLiquidRateSTDB.value,
      waterCutRatio.value
    );

    // Q_o
    const oilFlowRateSTBD = getOilFlowRateSTDB(
      desiredLiquidRateSTDB.value,
      waterCutRatio.value
    );

    const gasFlowRateSCFD = gas_phase_gas_rate_from_glr(
      desiredLiquidRateSTDB.value,
      gasLiquidRatioScfStb.value
    );

    return {
      ...wellTestFlowRates.value,
      gasFlowRateSCFD,
      oilFlowRateSTBD,
      waterFlowRateSTBD,
      liquidFlowRateSTBD: oilFlowRateSTBD + waterFlowRateSTBD,
      gasOilRatioSCFSTB:
        oilFlowRateSTBD === 0 ? 0 : gasFlowRateSCFD / oilFlowRateSTBD,
      gasLiquidRatioSCFSTB:
        gasFlowRateSCFD / (oilFlowRateSTBD + waterFlowRateSTBD)
    };
  });

  return {
    productivityIndexStbdPsia,
    totalLiquidFlowRateStbd,
    gasLiquidRatioScfStb,
    gasOilRatioScfStb,
    waterCutPercentage,
    datumPointFt,
    staticFluidLevelFeet,
    producingFluidLevelFeet,
    staticBottomholePressurePsia,
    producingBottomholePressurePsia,
    liquidDensityLbmFt3,
    tempGrad,
    customFluidProperties,
    customViscosityProperties,
    liquidDensityAtDatumLbmft3,
    hasDeviationSurvey,
    defaultEntryRows,
    entryRows,
    entryRowsSurfaceToBoe,
    entryRowsBotToBoe,
    entryRowsAtSeals,
    bottomholeTemperatureF,
    surfaceTemperatureF,
    desiredLiquidRateSTDB,
    waterCutRatio,
    waterSpecificGravity,
    oilAPI,
    oilSpecificGravity,
    adjustedStaticBottomholePressurePSIA,
    basicData,
    casingInteriorDiameterAtSealsIn,
    casingInteriorDiameterAtMotorIn,
    gasPercentages,
    hasGasCompositeValues,
    gasMolecularWeight,
    gasSpecificGravity,
    wellTestFlowRates,
    desiredFlowRates
  };
};

export type WellInputsCalcs = ReturnType<typeof defineWellInputs>;
